/**
 * Phase 13 / Task 13.3 (DEC-351) — data for the dispatcher Tanks tab.
 *
 * GET /v1/tanks/levels. Types mirror packages/api/src/routes/hauler-catalog/tank-levels.ts
 * (copied by hand, like every web-dispatch hook).
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, useApiQuery, type useApiOpts } from '@lodex-pro/web-shared';
import type { TankLatestReading } from '@lodex-pro/shared/tank-level-format';
import { USER_PROFILE_QUERY_KEY } from '../../lib/use-user-timezone';

export interface TankLevelRow {
  readonly tankId: string;
  readonly tankName: string;
  /** The tank's location name, or its standalone well's name. */
  readonly siteName: string | null;
  /** 2026-10-21 — the customer (operator) the tank is hauled for; null when unknown. Older APIs omit it. */
  readonly customerName?: string | null;
  /**
   * 2026-10-21 — first to last reading in the window, in hours. Under MIN_HISTORY_SPAN_HOURS the
   * tab shows no rate or projection (shared isHistoryTooShort). Older APIs omit it.
   */
  readonly historySpanHours?: number | null;
  /** BBL, numeric string; null when the tank has no capacity set. */
  readonly capacityBbl: string | null;
  /**
   * False when no bbl per inch is known, the tank's own or the standard catalog's single value
   * for its capacity (DEC-351, DEC-377), so no level can be derived.
   */
  readonly hasStrapping: boolean;
  /** DEC-377 — the tank's own bbl per inch; null = none entered. Older APIs omit these three. */
  readonly bblPerInch?: string | null;
  /** DEC-377 — where the estimate's bbl per inch came from; null = none known. */
  readonly bblPerInchSource?: 'TANK' | 'CATALOG' | null;
  /**
   * DEC-377 — with none of its own, the catalog's distinct values for it (its linked row, else
   * its capacity): one is the value its estimate uses, several are ambiguous.
   */
  readonly bblPerInchChoices?: readonly {
    readonly name: string;
    readonly capacityBbl: string;
    readonly bblPerInch: string;
  }[];
  /**
   * The latest recorded level, BBL: what a haul left the tank at, or a hand-entered reading
   * (13.2a), whichever is later; null when none is known.
   */
  readonly lastLevelBbl: number | null;
  /** When lastLevelBbl was recorded (ISO); null with no recorded level. Older APIs omit it. */
  readonly lastLevelAt?: string | null;
  /** ISO time of the last haul on this tank, any capture method. */
  readonly lastHaulAt: string | null;
  /** BBL per day; null means not enough history. Always null when estimates are off. */
  readonly fillRateBpd: number | null;
  /** Estimated BBL in the tank now; null when estimates are off or unknown. */
  readonly estimatedCurrentBbl: number | null;
  /** 0-100, from estimatedCurrentBbl and capacity; null when either is unknown. */
  readonly estimatedPercentFull: number | null;
  /** ISO time the tank is projected to be full; null when unknown or estimates are off. */
  readonly predictedFullAt: string | null;
  /** Phase 13.4a (0236) — the haul threshold, % full; null = none. Older APIs omit these. */
  readonly thresholdPercent?: number | null;
  readonly leadTimeHours?: number;
  /** When the tank is predicted to reach its threshold; a past time = at or over it. */
  readonly predictedThresholdAt?: string | null;
  /** Auto-haul is on for this tank (plan feature, site switch and a job type). */
  readonly autoHaulOn?: boolean;
  /** 13.4b (0237) — why the auto-haul sweeper is holding this tank; null = not held. */
  readonly autoHaulHoldReason?: string | null;
  /** 13.4b — the HaulId of the tank's open auto job. */
  readonly openAutoHaulId?: string | null;
  /**
   * Tank registry PR 1 (0245) — a reading was entered in inches but the tank has no bbl per inch
   * to turn it into barrels. Older APIs omit it.
   */
  readonly needsSize?: boolean;
  /**
   * 13.8 (D-23c) — the latest live reading, own or shared, as stored: raw (volumeBbl null) when
   * unconverted. Display only. Older APIs omit it.
   */
  readonly latestReading?: TankLatestReading | null;
  /**
   * 13.8 — the producer's latest shared reading is in inches and the producer's tank has no size
   * of its own, so it has no barrels. Older APIs omit it.
   */
  readonly operatorNeedsSize?: boolean;
}

export interface TankLevelsResponse {
  readonly generatedAt: string;
  /** hauler_tenant_settings.tank_estimates_enabled — off means recorded data only. */
  readonly estimatesEnabled: boolean;
  /** Soonest-full first; tanks without a prediction last. */
  readonly tanks: readonly TankLevelRow[];
}

export const TANK_LEVELS_QUERY_KEY = ['tanks', 'levels'] as const;

export function useTankLevels(
  apiOpts: ReturnType<typeof useApiOpts>,
): ReturnType<typeof useApiQuery<TankLevelsResponse>> {
  const qc = useQueryClient();
  const query = useApiQuery<TankLevelsResponse>(apiOpts, '/v1/tanks/levels', {
    queryKey: TANK_LEVELS_QUERY_KEY,
    refetchInterval: 60_000,
  });
  // §58 (#881 review): TANKS_TAB was turned off while the tab is open. Refetch the profile at
  // once, so the route sends the dispatcher back to Operations instead of a 403 every minute.
  const featureOff = query.error instanceof ApiError && query.error.code === 'FEATURE_NOT_ENABLED';
  React.useEffect(() => {
    if (featureOff) void qc.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEY });
  }, [featureOff, qc]);
  return query;
}
